import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { authAPI } from '../lib/api';

export default function ResetPasswordPage() {
  const { theme } = useApp();
  const primary = theme?.primary || '#2563eb';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromURL = searchParams.get('token') || '';

  const [form, setForm] = useState({ token: tokenFromURL, password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.token) {
      setError('Token de redefinição inválido.');
      return;
    }
    if (form.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (form.password !== form.confirm) {
      setError('As senhas não coincidem.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authAPI.resetPassword(form.token, form.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Token inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'var(--bg)',
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -20%, ${primary}22 0%, transparent 70%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
            >
              <Radio size={26} className="text-white" />
            </div>
            <span className="font-display font-black text-xl tracking-tight text-[var(--text-primary)]">
              OLT MANAGER
            </span>
          </Link>
        </div>

        <div className="rounded-2xl border border-[var(--border-mid)] bg-[var(--surface)] shadow-2xl p-8">
          {success ? (
            <div className="text-center">
              <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: primary }} />
              <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">Senha redefinida!</h2>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Sua senha foi alterada com sucesso. Redirecionando para o login...
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
                style={{ color: primary }}
              >
                Ir para o login agora
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Nova senha</h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  Defina uma nova senha para sua conta.
                </p>
              </div>

              {error && (
                <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                  <AlertCircle size={15} className="shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {!tokenFromURL && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                      Token de redefinição
                    </label>
                    <input
                      type="text"
                      value={form.token}
                      onChange={(e) => setForm({ ...form, token: e.target.value })}
                      placeholder="Cole o token recebido"
                      className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                    Nova senha
                  </label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Mínimo 8 caracteres"
                      className="w-full h-11 px-4 pr-11 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                    Confirmar nova senha
                  </label>
                  <input
                    type={showPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    placeholder="Repita a senha"
                    className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? 'Redefinindo...' : 'Redefinir senha'}
                </button>
              </form>

              <p className="mt-6 text-center text-xs">
                <Link
                  to="/login"
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  ← Voltar para o login
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
